// src/modules/clinical-notes-v2/hooks/useClinicalContextBundle.js
//
// Arma el `clinicalContext` que los Eps 2/3/4 del backend AI Clinical
// Notes esperan en el body de cada request.
//
// Por la decisión documentada en doc 17 §0 (item 1), el backend NO
// devuelve `chronicConditions / activeMedications / allergies /
// recentVitals / recentLabs` en el Ep 1 (`/patient-context/{cipId}`).
// El front es responsable de armarlas localmente desde servicios
// existentes (AdvancedMdContext + CodingOpportunities closed).
//
// Política PHI: el bloque `patient` se pasa por `sanitizePatientForAi`
// dentro del service — este hook entrega el shape completo, el wrapper
// del service descarta los campos sensibles antes de mandarlo a la IA.

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { AdvancedMdContext } from "../../../context/AdvancedMD/AdvancedMdContext";
import getCodingOpportunities from "../../../utils/services/CodingOpportunities/getCodingOpportunities";
import { getCareGapsByCip } from "../../../utils/services/APP/CareGAP/getCareGapsByCip";

import { getPatientContext, sanitizePatientForAi } from "../services/clinicalNotesAiService";

// =============================================================
// Mappers (servicios existentes -> shape del clinicalContext)
// =============================================================

// Shape real de AdvancedMD medications (GET /api/advancedmd/medications/{id}):
//   { id, drugName, drugStrength, doseForm, route, frequency,
//     quantity, refills, startDate, stopDate }
const mapMedication = (m) => {
  const drug = [m?.drugName, m?.drugStrength].filter(Boolean).join(" ");
  const dose = [m?.doseForm, m?.route, m?.frequency].filter(Boolean).join(" · ");
  return {
    name:      drug || m?.drugName || "",
    dose:      dose || "",
    startDate: m?.startDate ?? null,
    stopDate:  m?.stopDate  ?? null,
  };
};

// Shape real de AdvancedMD allergies:
//   { id, allergy, reaction, treatment, status }
const mapAllergy = (a) => ({
  substance: a?.allergy   ?? "",
  reaction:  a?.reaction  ?? "",
  severity:  a?.status    ?? "",  // AdvMD usa "status" — lo mapeamos como severity
  treatment: a?.treatment ?? "",
});

// Shape real de AdvancedMD labs:
//   { id, profileId, companyName, obsDateTime, specDateTime,
//     resultCode, resultDesc, resultUnits, resultRange, resultStatus, resultValue }
const mapLab = (l) => ({
  date:           l?.obsDateTime ?? l?.specDateTime ?? null,
  loinc:          l?.resultCode  ?? null,
  name:           l?.resultDesc  ?? "",
  value:          l?.resultValue ?? "",
  unit:           l?.resultUnits ?? "",
  abnormalFlag:   l?.resultStatus ?? null,
  referenceRange: l?.resultRange ?? null,
  companyName:    l?.companyName ?? "",
});

const mapChronicFromClosedCodingOp = (op) => ({
  icd10:       op?.suspectedIcd10 ?? op?.icd10 ?? op?.VAR_ICD10 ?? "",
  description: op?.suspectedDescription ?? op?.description ?? op?.VAR_DESCRIPTION ?? "",
});

/**
 * Mapea un care gap del endpoint legacy `/api/CareGap/patient/{cipId}`
 * (vía `getCareGapsByCip`) al shape que las cards del módulo v2 esperan.
 * El Ep 1 del backend AI hoy no devuelve los care gaps poblados para
 * todos los pacientes — usamos esta lista legacy como fuente confiable
 * de items + caemos a Ep 1 solo si lo del backend AI viene con datos.
 */
const mapLegacyCareGapToV2 = (row) => ({
  careGapId:   row.careGapId,
  measure:     row.careGapName || "—",
  qm:          null,
  icd10:       null,
  category:    null,
  openSince:   row.createdDate || row.lastUpdated || null,
  lastAttempt: row.lastUpdated || null,
  peerRate:    null,
  internalRate: null,
  // metadata para que el front pueda filtrar/mostrar si quiere
  _sourceStatusCode: row.sourceStatusCode,
  _workflowStatus:   row.workflowStatus,
  _measurementYear:  row.measurementYear,
});

/**
 * Filtra solo los care gaps abiertos. La fuente legacy expone
 * `sourceStatusCode` con valores "OPEN"/"CLOSED"/"SUBMITTED" (mismo
 * filtro que usa `src/modules/care-gaps/pages/CareGapsPage.jsx`).
 */
const filterOpenCareGaps = (rows) =>
  (rows || []).filter((r) => (r.sourceStatusCode || "").toUpperCase() === "OPEN");

// =============================================================
// Hook
// =============================================================

/**
 * Hook que arma el `clinicalContext` para enviar a Eps 2/3/4 del
 * backend AI Clinical Notes.
 *
 * Bundle resultante (shape compatible con doc 18 §4 body.clinicalContext):
 * {
 *   patient: { cipId, age, gender, memberType },  // sanitizado
 *   chronicConditions: [{ icd10, description }],
 *   activeMedications: [{ name, dose, startDate }],
 *   allergies: [{ substance, reaction, severity }],
 *   recentVitals: [],                              // v1: vacío (TODO)
 *   recentLabs: [{ date, loinc, name, value, unit, abnormalFlag, referenceRange }],
 *   openCareGaps: [...],                           // tal cual viene del Ep 1
 *   openCodingOpportunities: [...]                 // tal cual viene del Ep 1
 * }
 *
 * @param {Object} params
 * @param {string} params.cipId
 * @param {string} params.advancedMdPatientId
 * @param {Object} [params.demographics] - { age, gender, memberType } que ya tiene
 *   el Patient Map en su state (Redux). Si se provee, suplementa lo que el Ep 1
 *   devuelve para `patient`.
 * @param {boolean} [params.enabled=true] - si false, no dispara cargas.
 *
 * @returns {{
 *   bundle: Object | null,
 *   isLoading: boolean,
 *   error: Error | null,
 *   refetch: () => Promise<void>,
 * }}
 */
export function useClinicalContextBundle({
  cipId,
  advancedMdPatientId,
  demographics,
  enabled = true,
}) {
  const advmd = useContext(AdvancedMdContext);
  if (!advmd) {
    throw new Error(
      "useClinicalContextBundle: AdvancedMdProvider not mounted up the tree"
    );
  }

  const [patientContext, setPatientContext] = useState(null);
  const [closedCodingOps, setClosedCodingOps] = useState([]);
  const [legacyCareGaps, setLegacyCareGaps] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Para evitar dobles cargas en StrictMode dev
  const inFlightKey = useRef(null);

  const fetchAll = useCallback(async () => {
    if (!enabled || !cipId) return;

    const key = `${cipId}|${advancedMdPatientId || ""}`;
    if (inFlightKey.current === key) return;
    inFlightKey.current = key;

    setIsLoading(true);
    setError(null);

    try {
      // Lanzamos en paralelo todo lo que no depende entre sí.
      const promises = [getPatientContext(cipId)];

      // Coding opps cerrados → chronicConditions (aproximación v1)
      promises.push(
        getCodingOpportunities({ cipId, status: "Closed", pageSize: 50 })
          .catch((e) => {
            // No bloqueante; si falla, chronicConditions queda en []
            console.warn("useClinicalContextBundle: closed coding ops failed", e);
            return { data: [] };
          })
      );

      // Care gaps via endpoint legacy /api/CareGap/patient/{cipId} (mismo
      // que usa el Patient Map y el módulo Care Gaps). El Ep 1 del backend
      // AI hoy no entrega care gaps poblados para todos los pacientes —
      // ver doc 17 §0 #4. Usamos esta lista como fuente principal y caemos
      // a la del Ep 1 solo cuando ésta viene vacía.
      promises.push(
        getCareGapsByCip(cipId).catch((e) => {
          console.warn("useClinicalContextBundle: legacy care gaps failed", e);
          return [];
        })
      );

      // Si tenemos advancedMdPatientId y el context no tiene datos cargados,
      // disparamos los loads en background (no bloquean este fetch — el
      // bundle se reactualiza vía el `useMemo` cuando los states cambian).
      if (advancedMdPatientId) {
        if (!advmd.medications?.length) advmd.loadMedications?.(advancedMdPatientId);
        if (!advmd.allergies?.length)   advmd.loadAllergies?.(advancedMdPatientId);
        if (!advmd.labs?.length)        advmd.loadLabs?.(advancedMdPatientId);
      }

      const [ctx, closedOps, legacyGaps] = await Promise.all(promises);

      setPatientContext(ctx || null);
      setClosedCodingOps(Array.isArray(closedOps?.data) ? closedOps.data : []);
      setLegacyCareGaps(Array.isArray(legacyGaps) ? legacyGaps : []);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
      inFlightKey.current = null;
    }
  }, [enabled, cipId, advancedMdPatientId, advmd]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const bundle = useMemo(() => {
    if (!patientContext) return null;

    // El Ep 1 hidrata `patient` desde el primer coding opp; si está
    // vacío, completamos con lo que tenga el patient map.
    const rawPatient = {
      ...(patientContext.patient || {}),
      ...(demographics || {}),
    };

    // Source priority para care gaps:
    // 1) Lo que devuelva el Ep 1 si trae items (futuro: cuando el SP
    //    backend amplíe los campos qm/icd10/category/etc.)
    // 2) Fallback al endpoint legacy /api/CareGap/patient/{cipId},
    //    filtrado por sourceStatusCode === "OPEN"
    const ep1Gaps = patientContext.openCareGaps || [];
    const legacyOpen = filterOpenCareGaps(legacyCareGaps).map(mapLegacyCareGapToV2);
    const openCareGaps = ep1Gaps.length > 0 ? ep1Gaps : legacyOpen;

    return {
      patient: sanitizePatientForAi(rawPatient),
      chronicConditions: closedCodingOps.map(mapChronicFromClosedCodingOp),
      activeMedications: (advmd.medications || []).map(mapMedication),
      allergies:         (advmd.allergies   || []).map(mapAllergy),
      recentVitals: [], // TODO v1: extraer de las 4 listas de notas via clinicalNotesFormatter
      recentLabs:        (advmd.labs        || []).map(mapLab),
      openCareGaps,
      openCodingOpportunities:   patientContext.openCodingOpportunities   || [],
    };
  }, [patientContext, closedCodingOps, legacyCareGaps, advmd.medications, advmd.allergies, advmd.labs, demographics]);

  return { bundle, isLoading, error, refetch: fetchAll };
}

export default useClinicalContextBundle;
